import { Checkbox, DefaultButton, Facepile, Icon, IDropdownOption, IFacepilePersona, Persona, PersonaSize } from '@fluentui/react';
import { DateCustom } from '../../../lib/DateCustom';
import { parseDateTimeForList } from '../../../lib/Util';
import { DetailsListView, EntityGridListProps } from '../../Common/DetailsList/DetailListView';
import { useTranslation, Trans } from 'react-i18next';
import React, { useEffect } from 'react';
import DialogConfirmDelete from './DialogConfirmDelete';

export const ActivityGridList = (props: EntityGridListProps) => {
  const MAX_SELECTED = 100;
  const { t, i18n } = useTranslation();
  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
  const [currentPageItems, setCurrentPageItems] = React.useState<any[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState<boolean>(false);
  const [refreshPage, setRefreshPage] = React.useState<boolean>(false);

  const formatActivo = (item: any) => {
    if (item.act_activo === '1') {
      return <Icon iconName="CheckMark" className="color-success" />;
    } else {
      return <Icon iconName="Cancel" className="color-error" />;
    }
  };

  const formatDuracion = (item: any) => {
    return <span>{DateCustom.formatHorasToString(item.act_horas)}</span>;
  };

  const formatEquipo = (item: any) => {
    let equipo = item.equipo_actividad.split(' | ');
    var getInitials = function (string: string) {
      var names = string.split(' '),
          initials = names[0].substring(0, 1).toUpperCase();

      if (names.length > 1) {
        initials += names[1].substring(0, 1).toUpperCase();
      }
      return initials;
    };

    let equipoPersonas: IFacepilePersona[] = equipo.map((persona: string) => {
      return {
        personaName: persona,
        imageInitials: getInitials(persona),
      };
    });

    return (
        <Facepile
            personas={equipoPersonas}
            personaSize={10}
            maxDisplayablePersonas={6}
            overflowButtonType={1}
            overflowButtonProps={{ ariaLabel: 'Ver más' }}
            onRenderPersona={(props) => (
                <Persona {...props} size={PersonaSize.size24} onRenderPrimaryText={() => <></>}></Persona>
            )}
        />
    );
  };

  const handleSelectActivity = (item: any, checked?: boolean) => {
    if (checked) {
      if (selectedItems.length >= MAX_SELECTED) {
        props.courier.messageError(t('Se alcanzó el límite máximo de 100 filas seleccionadas'));
        return;
      }
      setSelectedItems((prev) => [...prev, item.act_id]);
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== item.act_id));
    }
  };

  const handleSelectAllActivities = (checked?: boolean) => {
    const currentPageIds = currentPageItems
      .filter((item) => item.isHabilitadoEliminar)
      .map((item) => item.act_id);

    if (checked) {
      const merged = Array.from(new Set([...selectedItems, ...currentPageIds]));
      if (merged.length > MAX_SELECTED) {
        props.courier.messageError(t('Se alcanzó el límite máximo de 100 filas seleccionadas'));
      }
      setSelectedItems(merged.slice(0, MAX_SELECTED));
    } else {
      setSelectedItems((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    }
  };

  const handleSetItems: React.Dispatch<React.SetStateAction<any[]>> = (value) => {
    const items = value instanceof Function ? value(currentPageItems) : value;
    setCurrentPageItems(items);
    if (props.setItems) {
      props.setItems(value);
    }
  };

  const handleConfirmDialog = async() => {
    setShowConfirmDialog(false);
    props.changeBlockUI(true);

    await props.system?.getActivitySystem().deleteActivities(selectedItems);

    setRefreshPage(!refreshPage);
    setCurrentPageItems([]);
    setSelectedItems([]);
    props.courier.messageSuccess(t('Se eliminaron las actividades seleccionadas'));
    props.changeBlockUI(false);
  }

  const handleCancelConfirmDialog = () => {
    setShowConfirmDialog(false);
  };

  const allCurrentPageItemsSelected =
      currentPageItems.length > 0 && currentPageItems.every((item) => selectedItems.includes(item.act_id));

  const someCurrentPageItemsSelected =
      currentPageItems.some((item) => selectedItems.includes(item.act_id)) && !allCurrentPageItemsSelected;

  const columns = [
    {
      fieldName: 'seleccionar_actividad',
      name: '',
      _onRender: (item: any) => (
          <div onClick={(event) => event.stopPropagation()}>
            <Checkbox
              ariaLabel={t('Seleccionar actividad')}
              checked={selectedItems.includes(item.act_id)}
              onChange={(event, checked) => {
                event?.stopPropagation();
                handleSelectActivity(item, checked);
              }}
              disabled={!item.isHabilitadoEliminar}
              styles={{
                checkbox: {
                  borderColor: '#c6c6c6',
                  cursor: item.isHabilitadoEliminar ? 'pointer' : 'not-allowed'
                },
              }}
            />
          </div>
      ),
      onRenderHeader: () => (
        <div onClick={(event) => event.stopPropagation()} style={{ marginTop: 15 }}>
          <Checkbox
            ariaLabel={t('Seleccionar todas las actividades')}
            checked={allCurrentPageItemsSelected}
            indeterminate={someCurrentPageItemsSelected}
            onChange={(event, checked) => {
              event?.stopPropagation();
              handleSelectAllActivities(checked);
            }}
            styles={{
              checkbox: {
                borderColor: '#c6c6c6'
              },
            }}
          />
        </div>
      ),
      maxWidth: 32,
      minWidth: 32,
      isResizable: false,
      isPadded: false,
      onColumnClick: () => {},
    },
    {
      fieldName: 'act_nombre',
      name: t('Nombre'),
    },
    {
      fieldName: 'act_inicio',
      name: t('Inicio'),
      _onRender: (item: any) => parseDateTimeForList(props.system, item.act_inicio),
      maxWidth: 110,
      minWidth: 110,
    },
    {
      fieldName: 'owner_actividad_nombre',
      name: t('Creador'),
    },
    {
      fieldName: 'pro_codigo',
      name: t('Proyecto Codigo'),
      maxWidth: 90,
      minWidth: 90,
    },
    {
      fieldName: 'pro_nombre',
      name: t('Proyecto nombre'),
    },
    {
      fieldName: 'act_horas',
      name: t('Duracion'),
      _onRender: (item: any) => formatDuracion(item),
      maxWidth: 50,
      minWidth: 50,
    },
    {
      fieldName: 'act_activo',
      name: t('Activo'),
      _onRender: (item: any) => formatActivo(item),
      maxWidth: 20,
      minWidth: 20,
    },
    {
      fieldName: 'equipo_actividad',
      name: t('Equipo'),
      _onRender: (item: any) => formatEquipo(item),
      onRenderPersona: () => <></>,
      minWidth: 180,
      maxWidth: 180,
    },
  ];

  const bulkActionBar = selectedItems.length > 0 ? (
      <div style={{ display: 'flex', justifyContent: "end",alignItems: 'center', gap: 12, padding: '8px 0' }}>
        <DefaultButton
            iconProps={{ iconName: 'Delete' }}
            text={t('Eliminar seleccionados')}
            styles={{
              root: { borderColor: '#d13438', color: '#d13438' },
              rootHovered: { borderColor: '#a4262c', color: '#a4262c', backgroundColor: '#fde7e9' },
              icon: { color: '#d13438' },
            }}
            onClick={() => setShowConfirmDialog(true)}
        />
        <span style={{ fontSize: 13, color: '#605e5c' }}>
          {selectedItems.length} {selectedItems.length === 1 ? t('fila seleccionada') : t('filas seleccionadas')}
        </span>
      </div>
  ) : undefined;

  const baseUrl = React.useMemo(() => {
    // setRefreshPage(!refreshPage);
    return props.baseUrl;
  }, [refreshPage]);

  return (
      <>
        <DialogConfirmDelete
          isOpen={showConfirmDialog}
          onConfirm={handleConfirmDialog}
          onCancel={handleCancelConfirmDialog}
        />
        <DetailsListView
            refresh={refreshPage}
            keyList={props.keyList}
            onClickCardMode={() => props.onClickCardMode()}
            onClickGridMode={() => props.onClickGridMode()}
            mode={props.mode}
            title={t('Actividades')}
            onItemClick={props.onItemClick}
            shouldUpdate={props.shouldUpdate}
            cols={columns}
            isGridList={true}
            listFilters={[]}
            quickFilters={props.quickFilters}
            baseUrl={baseUrl}
            changeBlockUI={props.changeBlockUI}
            courier={props.courier}
            setItems={handleSetItems}
            renderBeforeHeader={bulkActionBar}
        ></DetailsListView>
      </>
  );
};
